'use client';

import type { PrV2CommentTarget, PrV2Tab } from '@/lib/pr-v2/tabs';
import { useCallback, useEffect, useState } from 'react';

import { getPrV2CommentAnchorId, replacePrV2PageUrl } from '@/lib/pr-v2/comment-anchors';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';

// Once the Discussion tab is ready, how long to wait for the comment's thread to render.
const DISCUSSION_COMMENT_RENDER_TIMEOUT_MS = 2000;

type UseOpenPrV2CommentOptions = {
  /** Null on a pane without comments, such as /code's PR overview. */
  activeTab: PrV2Tab | null;
  /** The Discussion timeline has mounted and its data has loaded. */
  isDiscussionReady: boolean;
};

function updateUrlHash(elementId: string): void {
  const url = new URL(window.location.href);
  url.hash = elementId;
  replacePrV2PageUrl(url);
}

/** Scrolls to a Discussion comment once it renders; returns a cleanup that stops waiting. */
function scrollToDiscussionCommentOnceRendered(commentId: string, onDone: () => void): () => void {
  const elementId = getPrV2CommentAnchorId('discussion', commentId);
  let observer: MutationObserver | undefined;
  let firstCheckId: number | undefined;
  let timeoutId: number | undefined;

  const stopWaiting = () => {
    observer?.disconnect();
    window.clearTimeout(firstCheckId);
    window.clearTimeout(timeoutId);
  };
  const scrollIfRendered = () => {
    const element = document.getElementById(elementId);
    if (!element) {
      return;
    }

    stopWaiting();
    updateUrlHash(elementId);
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onDone();
  };

  observer = new MutationObserver(scrollIfRendered);
  observer.observe(document.body, { childList: true, subtree: true });
  // Check on the next tick rather than now, so an effect run that is immediately cleaned up (as
  // React Strict Mode does) never scrolls.
  firstCheckId = window.setTimeout(scrollIfRendered, 0);
  timeoutId = window.setTimeout(() => {
    stopWaiting();
    onDone();
  }, DISCUSSION_COMMENT_RENDER_TIMEOUT_MS);

  return stopWaiting;
}

/**
 * Opens one comment at a time on the PR page; a new request replaces the pending one. A request
 * waits, however long it takes, until its tab is active and ready: on Changes, until the diff pane
 * registers comment navigation (which then expands the file and waits for the diff); on
 * Discussion, until the timeline has mounted and its data has loaded, then briefly for the thread
 * to render.
 */
export function useOpenPrV2Comment({ activeTab, isDiscussionReady }: UseOpenPrV2CommentOptions): {
  requestComment: (target: PrV2CommentTarget) => void;
  abandonCommentsOutside: (tab: PrV2Tab | null) => void;
  /**
   * The Discussion comment waiting to open, until it opens or stops waiting. The timeline expands
   * a collapsed group that holds it, so the comment can render.
   */
  openingDiscussionCommentId: string | null;
} {
  const [pendingComment, setPendingComment] = useState<PrV2CommentTarget | null>(null);
  // The tab can also change without `abandonCommentsOutside`, e.g. through browser history.
  const [previousActiveTab, setPreviousActiveTab] = useState(activeTab);
  if (previousActiveTab !== activeTab) {
    setPreviousActiveTab(activeTab);
    if (pendingComment && pendingComment.tab !== activeTab) {
      setPendingComment(null);
    }
  }
  const navigateToComment = useDiffNavigationStore((state) => state.navigateToComment);
  const canNavigateToComment = useDiffNavigationStore(
    (state) => state.handlers.navigateToComment !== undefined,
  );

  // Leaving a tab abandons a comment still waiting to open on it.
  const abandonCommentsOutside = useCallback((tab: PrV2Tab | null) => {
    setPendingComment((pending) => (pending && pending.tab !== tab ? null : pending));
  }, []);

  useEffect(() => {
    if (!pendingComment || activeTab !== pendingComment.tab) {
      return;
    }

    const finish = () => {
      setPendingComment((pending) => (pending === pendingComment ? null : pending));
    };

    if (pendingComment.tab === PR_V2_TABS.DISCUSSION) {
      if (!isDiscussionReady) {
        return;
      }
      return scrollToDiscussionCommentOnceRendered(pendingComment.commentId, finish);
    }

    if (!canNavigateToComment) {
      return;
    }

    const { commentId, filename, targetHeadSha } = pendingComment;
    const timeoutId = window.setTimeout(() => {
      navigateToComment(commentId, {
        filename,
        source: 'programmatic',
        ...(targetHeadSha ? { targetHeadSha } : {}),
      });
      finish();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeTab, canNavigateToComment, isDiscussionReady, navigateToComment, pendingComment]);

  return {
    requestComment: setPendingComment,
    abandonCommentsOutside,
    openingDiscussionCommentId:
      pendingComment?.tab === PR_V2_TABS.DISCUSSION ? pendingComment.commentId : null,
  };
}
